import * as Sentry from "@sentry/vue";
import { getScanCombinedInfos, getStcConfigByKey } from "./services.js";

/**
 * Fetches branding configuration using scan ID and API key
 * @param {string} scan_id - The scan ID
 * @param {string} api_key - The API key
 * @returns {Object|null} - The branding theme or null if not found
 */
const getBrandingConfiguration = async (scan_id, api_key) => {
  try {
    const res = await getScanCombinedInfos(scan_id, api_key);
    if (res && res["auth-app-branding"]) {
      const theme = res["auth-app-branding"];
      setThemeColor(theme);
      return theme;
    }
    return null;
  } catch (error) {
    Sentry.captureException(new Error(`Error loading branding config: ${error.message}`));
    return null;
  }
};

/**
 * Fetches branding configuration using a StcConfig key
 * @param {string} key - The StcConfig key
 * @returns {Object|null} - The branding theme or null if not found
 */
const getBrandingConfigurationByKey = async (key) => {
  try {
    const config = await getStcConfigByKey(key);
    if (config && config["published"]) {
      const theme = config["published"]["auth-app-branding"];
      setThemeColor(theme);
      return theme;
    }
    return null;
  } catch (error) {
    Sentry.captureException(new Error(`Error fetching branding configuration by key: ${error}`));
    return null;
  }
};

/**
 * Sets CSS variables based on theme colors
 * @param {Object} theme - The theme object containing color values
 */
const setThemeColor = (theme) => {
  if (!theme || typeof theme !== "object") return;
  const root = document.documentElement;

  Object.entries(theme).forEach(([key, value]) => {
    if (typeof value === "string" && /^#([0-9A-F]{3}){1,2}$/i.test(value)) {
      root.style.setProperty(`--${key}`, value);
    }
  });

  if (theme.background && isDarkColor(theme?.background)) {
    root.style.setProperty("--text", "#ffffff");
  }
  if (theme?.primary && !isDarkColor(theme?.primary)) {
    root.style.setProperty("--text-button", "#222222");
  }
};

/**
 * Determines if a color is dark based on its luminance
 * @param {string} color - Hex color code (e.g., "#FFFFFF")
 * @returns {boolean} - True if the color is dark, false otherwise
 */
const isDarkColor = (color) => {
  const c = color.substring(1);
  const rgb = parseInt(c, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = (rgb >> 0) & 0xff;
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luma < 128;
};

export { getBrandingConfigurationByKey, getBrandingConfiguration };
